import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import {Card, CardContent, CardHeader, Grid, IconButton, LinearProgress, Stack, Tooltip, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {ArrowBack, PlaylistAddCheck} from '@mui/icons-material';
import {useAppDispatch} from '../store';
import {useAppTranslation} from '../services/i18n';
import {useIsKindOfAdmin, useMandatoryParams} from '../services/currentSpace';
import {isApiResultError} from '../helpers/api';
import {dateToGui, datetimeToGui, toCalendarDate} from '../helpers/date';
import {decimalNumber} from '../helpers/number';
import {fetchApprovalRequest} from 'store/approvals';
import {selectAuthUser} from 'store/selectors';
import {
    approvalJustification,
    approvalMissingFields,
    approvalSubjectLabel,
    ApprovalRequestDetail,
    isApprovable,
    isApprovalCancellable,
    isApprovalEditable,
    isApprovalPending,
    isLearningRequest
} from 'model/approval';
import {ApprovalApproveDialog} from 'components/approval/ApprovalApproveDialog';
import {ApprovalCancelDialog} from 'components/approval/ApprovalCancelDialog';
import {ApprovalRejectDialog} from 'components/approval/ApprovalRejectDialog';
import {ApprovalFileRow, UserAttributeCell} from 'components/approval/ApprovalReadOnly';
import {ApprovalStatusTag} from 'components/approval/ApprovalStatusTag';
import {ApprovalValidityBadge} from 'components/approval/ApprovalValidityBadge';
import {ReadOnlyField} from 'components/ReadOnlyField';

/** Trims the seconds the API sends on `subject_start_time` / `subject_end_time`. */
const toShortTime = (value?: string | null) => value?.slice(0, 5);

/**
 * The term as submitted: a single day or a range, plus the time of day when the
 * requester gave one. Only completions are always single-day.
 */
function approvalTerm(detail: ApprovalRequestDetail): string {
    const days = [dateToGui(toCalendarDate(detail.subject_date)), dateToGui(toCalendarDate(detail.subject_end_date))]
        .filter(Boolean).filter((day, i, all) => all.indexOf(day) === i).join(' – ');
    const times = [toShortTime(detail.subject_start_time), toShortTime(detail.subject_end_time)]
        .filter(Boolean).join('–');
    return [days, times].filter(Boolean).join(', ');
}

/**
 * Where the back button leads.
 *
 * The student's own screens carry their list in the path itself -- the detail is
 * mounted under `/dashboard/...` precisely so the left menu keeps Studium active
 * -- so the path answers it before `?from` is consulted at all. `?from` remains
 * for the openers that reach the manager route: the user-detail dialog, and
 * links still in circulation from before the student detail moved.
 */
export const backTargetFromParam = (from: string | null, pathname?: string): string => {
    if (pathname?.startsWith('/dashboard/requests')) {
        return '#/dashboard/requests';
    }
    if (pathname?.startsWith('/dashboard/approvals')) {
        return '#/dashboard/approvals';
    }
    if (from?.startsWith('user:')) {
        // the user-detail dialog
        return '#/userManagement';
    }
    // the student has no access to the management list
    if (from === 'student') {
        return '#/dashboard/approvals';
    }
    return from === 'requests' ? '#/dashboard/requests' : '#/approvals';
};

/**
 * Where this record's detail lives. The edit form is registered under the
 * manager route only, so a record opened from one of the student's own screens
 * has to carry its origin there and back -- `?from` is what travels.
 */
export const approvalDetailUrl = (id: string | undefined, from: string | null): string => {
    if (from === 'requests') {
        return '#/dashboard/requests/' + id;
    }
    if (from === 'student') {
        return '#/dashboard/approvals/' + id;
    }
    return '#/approvals/' + id;
};

/** The reverse: the `?from` value standing for the route this detail is on. */
const fromParamForPath = (pathname: string): string | null => {
    if (pathname.startsWith('/dashboard/requests')) {
        return 'requests';
    }
    return pathname.startsWith('/dashboard/approvals') ? 'student' : null;
};

/**
 * Muted red, as drawn -- rejecting and withdrawing should not read louder than
 * the solid green approve next to them.
 */
const dangerButtonStyle = {
    background: 'var(--color-error-back)',
    color: '#A32409',
    '&:hover': {background: '#FBD5CE'},
};

/**
 * The default disabled contained button is near-invisible on the black header,
 * so an unapprovable request keeps a readable, plainly inactive button instead.
 */
const approveButtonStyle = {
    '&.Mui-disabled': {background: 'rgba(255, 255, 255, 0.16)', color: 'var(--color-white50)'},
};

export const ApprovalDetailPage = () => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const {approvalId} = useParams();
    const [searchParams] = useSearchParams();
    const {pathname} = useLocation();
    const isKindOfAdmin = useIsKindOfAdmin();
    const mandatoryParams = useMandatoryParams();
    const authUser = useSelector(selectAuthUser);

    const [detail, setDetail] = useState<ApprovalRequestDetail>();
    const [isLoading, setIsLoading] = useState(true);
    const [isCancelOpen, setIsCancelOpen] = useState(false);
    const [isApproveOpen, setIsApproveOpen] = useState(false);
    const [isRejectOpen, setIsRejectOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!approvalId) {
            return;
        }
        setIsLoading(true);
        dispatch(fetchApprovalRequest({id: approvalId}))
            .then((res) => {
                setDetail(isApiResultError(res) ? undefined : (res.payload as ApprovalRequestDetail));
            })
            .finally(() => setIsLoading(false));
    }, [approvalId, dispatch, refreshKey]);

    const handleBack = useCallback(() => {
        window.location.href = backTargetFromParam(searchParams.get('from'), pathname);
    }, [searchParams, pathname]);

    const handleEdit = useCallback(() => {
        const from = fromParamForPath(pathname);
        window.location.href = '#/approvals/' + approvalId + '/edit' + (from ? '?from=' + from : '');
    }, [approvalId, pathname]);

    const handleDecided = useCallback(() => setRefreshKey(k => k + 1), []);

    if (isLoading) {
        return <LinearProgress/>;
    }

    if (!detail) {
        // covers both a missing record and one outside the user's management scope
        return <div className={'kws-content-margin'}>
            <div className={'data-grid-zero-data'}>{t('approvals.notFound')}</div>
        </div>;
    }

    const competences = detail.competences?.map(c => c.name).join(', ');
    // a request's checklist wants the target level as well, the way the form's own chips carry it
    const requestCompetences = detail.competences?.map(c => c.competence_level
        ? c.name + ' · ' + t('approvals.form.competenceLevelShort', {level: c.competence_level})
        : c.name).join(', ');
    const price = (detail.price || detail.price === 0) ? decimalNumber(detail.price, 0) : undefined;
    const attachments = detail.attachments?.length
        ? detail.attachments.map(f => <ApprovalFileRow key={f.id} file={f}/>)
        : undefined;
    /** who the record is for -- the first row of either kind of detail */
    const userFields = <>
        <Grid size={{xs: 12, md: 3}}>
            <ReadOnlyField label={t('approvals.field.fullName')} value={detail.user?.full_name}/>
        </Grid>
        {/* only the attributes this space actually collects, like the AngularJS user screens */}
        <UserAttributeCell show={mandatoryParams.personal_id}>
            <ReadOnlyField label={t('approvals.field.personalId')} value={detail.user?.personal_id}/>
        </UserAttributeCell>
        <UserAttributeCell show={mandatoryParams.department}>
            <ReadOnlyField label={t('approvals.field.department')} value={detail.user?.department}/>
        </UserAttributeCell>
        <UserAttributeCell show={mandatoryParams.job_title}>
            <ReadOnlyField label={t('approvals.field.jobTitle')} value={detail.user?.job_title}/>
        </UserAttributeCell>
    </>;
    // a learning request carries the requester's justification and, once decided,
    // the manager's reason; a course completion carries a certificate instead
    const isRequest = isLearningRequest(detail);
    const certificate = detail.external_certificate;
    // cancelling runs through the user endpoint, which only lets the owner through
    const isOwn = !!authUser?.id && detail.user?.id === authUser.id;
    const isOwnPending = isOwn && isApprovalCancellable(detail);
    /**
     * `ApprovalRequestPolicy#show?` lets a pending request through for its owner
     * or for someone who manages approvals -- so a non-owner who got this far is
     * a manager by definition, and an admin may also decide a request of their own.
     */
    const canDecide = isApprovalPending(detail) && (isKindOfAdmin || !isOwn);
    // the detail carries the competences, so the checklist resolves down to the
    // fields actually missing rather than the full list of five
    const missing = canDecide && !isApprovable(detail) ? approvalMissingFields(detail) : [];
    const missingHint = missing.length
        ? t('approvals.hint.missingFields', {fields: missing.map(key => t(key)).join(', ')})
        : t('approvals.action.approveIncomplete');
    /**
     * A decided request is read-only and offers nothing else, so the header would
     * otherwise be empty -- approved included, which is where deciding from this
     * screen lands. A completion is excluded: it stays editable in every status.
     */
    const isClosed = isLearningRequest(detail) && !isApprovalPending(detail);

    return <div className={'layout-fill layout-column tw-absolute'}>
        <div className={'kws-content-margin flex tw-h-100'}>
            <Card className={'tw-h-full tw-flex tw-flex-col'}>
                <CardHeader
                    className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                    title={<>
                        <IconButton onClick={handleBack} color={'inherit'} aria-label={t('approvals.action.back')}>
                            <ArrowBack/>
                        </IconButton>
                        <PlaylistAddCheck/>
                        <span>{approvalSubjectLabel(detail)}</span>
                        {/* a request carries who filed it as a field of its own, next to
                            who decided it -- a tooltip here would be unreachable on touch */}
                        <span className={'tw-ml-12'}><ApprovalStatusTag status={detail.status}/></span>
                        {/* a completion is filed and approved in one act, so the two names
                            never differ -- it reads as one line rather than a decision field */}
                        {!isRequest && !!detail.created_by && <span className={'tw-ml-12 tw-text-12'}>
                            {t('approvals.detail.createdAndApprovedBy', {
                                name: detail.created_by.full_name,
                                date: datetimeToGui(detail.created_at)
                            })}
                        </span>}
                    </>}
                    action={<Stack direction={'row'} sx={{gap: 1}} className={'tw-flex-wrap'}>
                        {/* gap rather than the spacing prop: the header wraps on a narrow
                            window, and this MUI's margin-based spacing (no useFlexGap
                            before 5.13) would leave the second row without one */}
                        {isKindOfAdmin && isApprovalEditable(detail) && <Button color={'primary'} variant={'contained'} className={'tw-w-auto'}
                            onClick={handleEdit}>{t('approvals.action.edit')}</Button>}
                        {/* the requester may withdraw their own request until it is decided */}
                        {isOwnPending && <Button variant={'contained'} className={'tw-w-auto'}
                            onClick={() => setIsCancelOpen(true)}
                            sx={dangerButtonStyle}>{t('approvals.action.cancel')}</Button>}
                        {/* the decision pair, in the drawn order: muted reject, solid approve */}
                        {canDecide && <Button variant={'contained'} className={'tw-w-auto'}
                            onClick={() => setIsRejectOpen(true)}
                            sx={dangerButtonStyle}>{t('approvals.action.reject')}</Button>}
                        {canDecide && <Tooltip title={isApprovable(detail)
                            ? t('approvals.action.approve')
                            : missingHint}>
                            {/* the span keeps the tooltip alive while the button is disabled */}
                            <span>
                                <Button color={'primary'} variant={'contained'} className={'tw-w-auto'}
                                    disabled={!isApprovable(detail)} sx={approveButtonStyle}
                                    onClick={() => setIsApproveOpen(true)}>{t('approvals.action.approve')}</Button>
                            </span>
                        </Tooltip>}
                        {/* white, as drawn -- a closed record offers a way out, not an action */}
                        {isClosed && <Button color={'inherit'} variant={'contained'} className={'tw-w-auto'}
                            onClick={handleBack}>{t('approvals.action.backToList')}</Button>}
                    </Stack>}
                />
                <CardContent className={'tw-p-32 tw-flex-grow tw-flex tw-flex-col tw-overflow-auto'}>
                    {/* what stands between this request and a decision -- the same
                        checklist the disabled Schválit reports, spelled out where
                        the manager can act on it */}
                    {!!missing.length && <div className={'tw-rounded-md tw-py-8 tw-px-12 tw-mb-24 tw-text-12'}
                        style={{background: 'var(--color-warning-back)', color: '#8A5A00'}}>
                        {missingHint}
                    </div>}
                    {isRequest
                        // one order in every state, and the same one the form collects in:
                        // who it is for, what it is about, why, and what a decision waits for
                        ? <>
                            <Grid container spacing={3}>
                                {userFields}
                                <Grid size={{xs: 12, md: 3}}>
                                    <ReadOnlyField label={t('approvals.field.mode')}
                                        value={t('approvals.type.' + detail.request_type)}/>
                                </Grid>
                                {/* what a thematic request was filed about, before a course answered it */}
                                {detail.request_type === 'request_topic' && <Grid size={{xs: 12, md: 9}}>
                                    <ReadOnlyField label={t('approvals.field.topic')} value={detail.subject_content}/>
                                </Grid>}
                                <Grid size={12}>
                                    <ReadOnlyField label={t('approvals.field.courseName')} value={detail.subject_title}/>
                                </Grid>
                                <Grid size={12}>
                                    <ReadOnlyField label={t('approvals.field.justification')}
                                        value={approvalJustification(detail)}/>
                                </Grid>

                                {/* the four the checklist insists on stay outside the optional box */}
                                <Grid size={{xs: 12, md: 4}}>
                                    <ReadOnlyField label={t('approvals.field.competencesWithLevel')}
                                        value={requestCompetences}/>
                                </Grid>
                                <Grid size={{xs: 6, md: 2}}>
                                    <ReadOnlyField label={t('approvals.field.termFrom')}
                                        value={dateToGui(toCalendarDate(detail.subject_date))}/>
                                </Grid>
                                <Grid size={{xs: 6, md: 2}}>
                                    <ReadOnlyField label={t('approvals.field.termTo')}
                                        value={dateToGui(toCalendarDate(detail.subject_end_date))}/>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <ReadOnlyField label={t('approvals.field.pricePerStudent')} value={price}/>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <ReadOnlyField label={t('approvals.field.provider')} value={detail.subject_provider}/>
                                </Grid>

                                {/* who filed it -- a manager may have filed on the student's
                                    behalf, so the name is not always the requester's own */}
                                <Grid size={{xs: 12, md: 4}}>
                                    <ReadOnlyField label={t('approvals.field.createdBy')}
                                        value={[detail.created_by?.full_name, datetimeToGui(detail.created_at)]
                                            .filter(Boolean).join(', ')}/>
                                </Grid>
                                {/* the decision itself -- without it a rejected request looks
                                    identical to a pending one once you open it */}
                                {!!detail.decided_at && <Grid size={{xs: 12, md: 4}}>
                                    <ReadOnlyField label={t('approvals.field.decidedBy')}
                                        value={[detail.decided_by?.full_name, datetimeToGui(detail.decided_at)]
                                            .filter(Boolean).join(', ')}/>
                                </Grid>}
                                {detail.status === 'rejected' && <Grid size={{xs: 12, md: 4}}>
                                    <ReadOnlyField label={t('approvals.field.rejectionReason')} value={detail.reason}/>
                                </Grid>}
                            </Grid>

                            {/* only what a decision does not wait for -- on the same grid as
                                the rows above, so the columns keep one baseline throughout */}
                            <div className={'tw-border tw-border-solid tw-rounded-md tw-p-24 tw-mt-32'}
                                style={{borderColor: 'var(--color-black-inactive)'}}>
                                <Grid container spacing={3}>
                                    <Grid size={12}>
                                        <Typography component={'div'}>{t('approvals.detail.details')}</Typography>
                                    </Grid>
                                    {/* the empty cell keeps the times under the term they belong to */}
                                    <Grid size={{md: 4}} sx={{display: {xs: 'none', md: 'block'}}}/>
                                    <Grid size={{xs: 6, md: 2}}>
                                        <ReadOnlyField label={t('approvals.field.timeFrom')}
                                            value={toShortTime(detail.subject_start_time)}/>
                                    </Grid>
                                    <Grid size={{xs: 6, md: 2}}>
                                        <ReadOnlyField label={t('approvals.field.timeTo')}
                                            value={toShortTime(detail.subject_end_time)}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <ReadOnlyField label={t('approvals.field.location')} value={detail.subject_location}/>
                                    </Grid>
                                    {/* both stay in the first column, one under the other; the
                                        hint under Poznámka belongs to the input, not to a value */}
                                    <Grid size={{xs: 12, md: 4}}>
                                        <Stack spacing={3}>
                                            <ReadOnlyField label={t('approvals.field.note')} value={detail.note}/>
                                            <ReadOnlyField label={t('approvals.requests.attachments')} value={attachments}/>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </div>
                        </>
                        // a completion is a course that already happened, recorded with its
                        // certificate -- none of the request's realization half applies
                        : <>
                            <Grid container spacing={3}>
                                {userFields}
                                <Grid size={{xs: 12, md: 6}}>
                                    <Stack spacing={3}>
                                        <ReadOnlyField label={t('approvals.field.subjectTitle')} value={detail.subject_title}/>
                                        <ReadOnlyField label={t('approvals.field.subjectDate')} value={approvalTerm(detail)}/>
                                    </Stack>
                                </Grid>
                                <Grid size={{xs: 12, md: 6}}>
                                    <Stack spacing={3}>
                                        <ReadOnlyField label={t('approvals.field.certificate')}
                                            value={detail.certificate ? <ApprovalFileRow file={detail.certificate}/> : undefined}/>
                                        <ReadOnlyField label={t('approvals.field.attachments')} value={attachments}/>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <div className={'tw-border tw-border-solid tw-rounded-md tw-p-24 tw-mt-32'}
                                style={{borderColor: 'var(--color-black-inactive)'}}>
                                <Grid container spacing={3}>
                                    <Grid size={12}>
                                        <Typography component={'div'}>{t('approvals.field.details')}</Typography>
                                    </Grid>
                                    {/* laid out row by row so the three columns stay on one baseline
                                        even though the expiration hint makes its cell taller */}
                                    <Grid size={{xs: 12, md: 4}}>
                                        <ReadOnlyField label={t('approvals.field.provider')} value={detail.subject_provider}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <ReadOnlyField label={t('approvals.field.studyTime')} value={detail.study_time}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        {/* expiration and budget live on the certificate association,
                                            not on the record itself */}
                                        {/* the exact date stays -- the badge only adds the
                                            at-a-glance state next to it */}
                                        <ReadOnlyField label={t('approvals.field.expiration')}
                                            value={certificate?.expiration_date
                                                ? <span className={'tw-flex tw-items-center tw-gap-8 tw-flex-wrap'}>
                                                    {dateToGui(toCalendarDate(certificate.expiration_date))}
                                                    <ApprovalValidityBadge expiration={certificate.expiration_date}/>
                                                </span>
                                                : undefined}
                                            hint={t('approvals.hint.expiration')}/>
                                    </Grid>

                                    <Grid size={{xs: 12, md: 4}}>
                                        <ReadOnlyField label={t('approvals.field.content')} value={detail.subject_content}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <ReadOnlyField label={t('approvals.field.form')}
                                            value={detail.form ? t('approvals.formValue.' + detail.form) : undefined}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        {/* unlike the budget, the price is the student's own number to see */}
                                        <ReadOnlyField label={t('approvals.field.price')} value={price}/>
                                    </Grid>

                                    <Grid size={{xs: 12, md: 4}}>
                                        <ReadOnlyField label={t('approvals.field.competences')} value={competences}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        {isKindOfAdmin && <ReadOnlyField label={t('approvals.field.budget')}
                                            value={certificate?.budget}/>}
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}/>
                                    <Grid size={12}>
                                        <ReadOnlyField label={t('approvals.field.note')} value={detail.note} hint={t('approvals.hint.note')}/>
                                    </Grid>
                                </Grid>
                            </div>
                        </>}
                </CardContent>
            </Card>
        </div>
        {isCancelOpen && !!authUser?.id && <ApprovalCancelDialog
            item={{id: detail.id, subject_title: detail.subject_title, subject_content: detail.subject_content}}
            userId={authUser.id}
            onSuccess={handleDecided}
            onClose={() => setIsCancelOpen(false)}/>}
        {/* a single record, so never the bulk endpoint -- see ApprovalApproveDialog */}
        {isApproveOpen && <ApprovalApproveDialog items={[detail]}
            onSuccess={handleDecided} onClose={() => setIsApproveOpen(false)}/>}
        {isRejectOpen && <ApprovalRejectDialog item={detail}
            onSuccess={handleDecided} onClose={() => setIsRejectOpen(false)}/>}
    </div>;
};
